import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { Grid, Autocomplete, TextField, InputAdornment, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const InvoiceSearch = forwardRef(({ onSelect, placeholder }, ref) => {
  const [productos, setProductos] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [open, setOpen] = useState(false);

  const API_TOKEN = process.env.REACT_APP_API_TOKEN || "";

  useImperativeHandle(ref, () => ({
    clear: () => {
      setBusqueda("");
      setProductos([]);
      setOpen(false);
      onSelect?.(null);
    },
  }));

  useEffect(() => {
    if (busqueda.length < 2) {
      setProductos([]);
      return;
    }

    let active = true;

    (async () => {
      try {
        const resp = await fetch(`/proformas/buscar-items/?q=${encodeURIComponent(busqueda)}`, {
          headers: { "X-API-TOKEN": API_TOKEN },
        });
        const { results = [] } = await resp.json();
        if (active) {
          setProductos(Array.isArray(results) ? results : []);
          setOpen(true);
        }
      } catch {
        if (active) setProductos([]);
      }
    })();

    return () => {
      active = false;
    };
  }, [busqueda, API_TOKEN]);

  const optionLabel = (opt) => {
    if (!opt) return "";
    if (typeof opt === "string") return opt;

    if (opt.type === "product" && opt.variant_id) {
      const baseName = (opt.name || "").split(" (")[0];
      return `${opt.code || ""} ${baseName} (${opt.sku || ""} ${opt.value || ""})`.trim();
    }

    return `${opt.code || ""} ${opt.name || ""}`.trim();
  };

  const handleSelect = (_, producto) => {
    if (producto && typeof producto === "object") {
      const selectedItem = {
        ...producto,
        id: producto.id ?? null,
        product_id: producto.product_id ?? null,
        variant_id: producto.variant_id ?? null,
        code: producto.code || "",
        name: producto.name || "",
        sku: producto.sku || "",
        value: producto.value || "",
        type: producto.type || "",
        stock: producto.stock ?? 0,
        codigo_principal:
          producto.codigo_principal ||
          (producto.type === "product" && producto.variant_id && producto.code && producto.sku
            ? `${producto.code}-${producto.sku}`
            : producto.code || ""),
        codigo_auxiliar: producto.codigo_auxiliar || "",
      };

      onSelect?.(selectedItem);
      setBusqueda(optionLabel(selectedItem));
    } else {
      onSelect?.(null);
      setBusqueda("");
    }

    setOpen(false);
  };

  const clearAll = () => {
    setBusqueda("");
    setProductos([]);
    setOpen(false);
    onSelect?.(null);
  };

  return (
    <Grid item xs={12} md={6}>
      <Autocomplete
        options={productos}
        filterOptions={(x) => x}
        getOptionLabel={optionLabel}
        isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
        inputValue={busqueda}
        onInputChange={(_, v, reason) => {
          if (reason === "input") {
            setBusqueda(v);
            setOpen(v.length >= 2);
          }
        }}
        open={open}
        onChange={handleSelect}
        noOptionsText="No se han encontrado productos"
        freeSolo
        disableClearable
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder || "Buscar producto por código, barras o nombre"}
            fullWidth
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={clearAll} edge="end">
                    <CloseIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}
      />
    </Grid>
  );
});

export default InvoiceSearch;